import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Share2, ChevronRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * /tie/:tieId — public deep-link page for one Team Tie (3 seed matches).
 * Consumes /api/ties/{id} and refreshes on set_scored / finalized SSE.
 * Sharable via WhatsApp / Twitter / IG for owners to broadcast a live tie.
 */
export default function TieView() {
  const { tieId } = useParams();
  const [tie, setTie] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/public/ties/${tieId}`); setTie(data); setErr(""); }
    catch (e) { setErr(formatApiError(e)); }
  }, [tieId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    let es = null;
    try {
      es = new EventSource(`${API_BASE}/public/events/stream`);
      const refresh = () => { if (alive) load(); };
      es.addEventListener("match.set_scored", refresh);
      es.addEventListener("match.finalized", refresh);
    } catch { /* silent */ }
    return () => { alive = false; try { es && es.close(); } catch { /* noop */ } };
  }, [load]);

  const share = async () => {
    const url = window.location.href;
    const text = tie ? `${tie.team_a?.name || "A"} vs ${tie.team_b?.name || "B"} — ${tie.total_games_a}-${tie.total_games_b} · Mara Padel League` : "Mara Padel League Tie";
    try {
      if (navigator.share) { await navigator.share({ title: text, url }); }
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch { /* user cancelled */ }
  };

  if (err) return <div className="p-10 text-red-400" data-testid="tie-error">{err}</div>;
  if (!tie) return <div className="p-10 text-zinc-500" data-testid="tie-loading">Loading tie…</div>;

  const s = (tie.status || "scheduled").toLowerCase();
  const badge = s === "live"
    ? { label: "🔴 LIVE", cl: "bg-red-500/20 text-red-300 border-red-500/40 animate-pulse" }
    : s === "finished"
    ? { label: "FINAL", cl: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" }
    : { label: "SCHEDULED", cl: "bg-white/10 text-white/70 border-white/10" };
  const gd = tie.game_difference || 0;

  return (
    <div className="min-h-screen bg-[#0a1610]">
      <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="tie-view">
        <div className="mb-4 flex items-center justify-between">
          <Link to="/" className="text-zinc-400 hover:text-white inline-flex items-center text-sm" data-testid="tie-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Mara Padel League
          </Link>
          <Button variant="outline" onClick={share} className="border-white/20 text-white" data-testid="tie-share">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">
              Team Tie · {(tie.matches?.[0]?.stage || "").toUpperCase()}
            </div>
            <h1 className="font-anton text-4xl md:text-6xl uppercase tracking-tighter text-white mt-1 leading-none">
              {tie.team_a?.name || "Team A"} <span className="text-zinc-500">vs</span> {tie.team_b?.name || "Team B"}
            </h1>
          </div>
          <Badge className={`text-sm ${badge.cl}`} data-testid="tie-status">{badge.label}</Badge>
        </div>

        {/* Aggregate scoreboard */}
        <Card className="p-6 mb-6 bg-[#0d1a13] border-white/10">
          <div className="grid grid-cols-3 items-center gap-4">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{tie.team_a?.name || "Team A"}</div>
              <div className="font-anton text-6xl text-white leading-none mt-1" data-testid="tie-games-a">{tie.total_games_a || 0}</div>
              <div className="text-[10px] text-zinc-500 mt-1">Games</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Game Difference</div>
              <div className={`font-anton text-6xl leading-none mt-1 ${gd > 0 ? "text-emerald-300" : gd < 0 ? "text-red-300" : "text-white"}`} data-testid="tie-gd">
                {gd > 0 ? "+" : ""}{gd}
              </div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{tie.team_b?.name || "Team B"}</div>
              <div className="font-anton text-6xl text-white leading-none mt-1" data-testid="tie-games-b">{tie.total_games_b || 0}</div>
              <div className="text-[10px] text-zinc-500 mt-1">Games</div>
            </div>
          </div>
        </Card>

        {/* Seed matches */}
        <div className="space-y-3" data-testid="tie-seeds">
          {(tie.matches || []).map(m => <SeedRow key={m.id} m={m} teamA={tie.team_a} teamB={tie.team_b} />)}
        </div>

        <div className="mt-8 text-center text-[10px] text-zinc-600 uppercase tracking-widest">
          Live via SSE · marapadelleague.com
        </div>
      </div>
    </div>
  );
}

function SeedRow({ m, teamA, teamB }) {
  const s = (m.status || "scheduled").toLowerCase();
  const isSet = m.format_type === "set_based";
  const setStrs = isSet ? (m.set_scores || []) : [];
  const label = s === "live" ? "LIVE" : s === "finished" ? "FINAL" : "TBD";
  const color = s === "live" ? "text-red-300" : s === "finished" ? "text-emerald-300" : "text-zinc-500";
  return (
    <Card className="p-4 bg-[#0f1a15] border-white/10 hover:border-white/20 transition-colors" data-testid={`tie-seed-${m.seed}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-[#F4B400]/15 border border-[#F4B400]/40 flex items-center justify-center font-anton text-2xl text-[#F4B400]">
            {m.seed || "?"}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Seed {m.seed}</div>
            <div className="text-sm text-white">{m.court || "TBD"}</div>
          </div>
        </div>
        <div className={`text-[10px] uppercase tracking-widest font-bold ${color}`}>
          {s === "live" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5 animate-pulse" />}
          {label}
        </div>
      </div>
      {isSet ? (
        <div className="grid grid-cols-3 gap-2">
          {setStrs.map((sc) => (
            <div key={sc.set} className="bg-black/30 rounded p-2 text-center" data-testid={`tie-seed-${m.seed}-set-${sc.set}`}>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">Set {sc.set}</div>
              <div className="font-anton text-2xl text-white leading-none mt-0.5">
                {sc.a}<span className="text-zinc-500 mx-1">-</span>{sc.b}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-2">
          <div className="font-anton text-3xl text-white">{m.score_a || 0} <span className="text-zinc-500">:</span> {m.score_b || 0}</div>
          <div className="text-[10px] text-zinc-500 mt-1">Time-based match</div>
        </div>
      )}
      <div className="mt-3 flex justify-end">
        <Link to={`/watch/${m.id}`} className="text-xs text-[#F4B400] hover:text-[#F4B400]/80 inline-flex items-center" data-testid={`tie-seed-watch-${m.seed}`}>
          Watch this match <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Link>
      </div>
    </Card>
  );
}
